/**
 * PageLayout Component
 *
 * Wrapper component that provides consistent page structure with sidebar.
 * Handles responsive layout for desktop and mobile.
 */

import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import Sidebar, { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_EXPANDED_WIDTH, SIDEBAR_PIN_KEY } from './Sidebar'
import MobileDrawer from './MobileDrawer'
import Footer from '../Footer'

export default function PageLayout({
  children,
  title,
  subtitle,
  headerActions,
  user,
  onLogout,
  showFooter = true,
  fillHeight = false,
  className
}) {
  // Track pin state to adjust content margin
  const [isPinned, setIsPinned] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem(SIDEBAR_PIN_KEY) === 'true'
  })

  // Listen for pin state changes via storage event + custom event (same-tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === SIDEBAR_PIN_KEY) {
        setIsPinned(e.newValue === 'true')
      }
    }
    const handleSidebarPinChange = (e) => setIsPinned(e.detail.pinned)

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('sidebar-pin-change', handleSidebarPinChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('sidebar-pin-change', handleSidebarPinChange)
    }
  }, [])

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <Sidebar user={user} onLogout={onLogout} />
      </div>

      {/* Mobile Drawer - shown only on mobile */}
      <div className="md:hidden">
        <MobileDrawer user={user} onLogout={onLogout} />
      </div>

      {/* Main Content Area */}
      <main
        className={cn(
          'flex flex-col transition-all duration-200 ease-out',
          fillHeight ? 'h-screen' : 'min-h-screen',
          // Desktop: offset by sidebar width
          'md:ml-14', // 56px = SIDEBAR_COLLAPSED_WIDTH
          isPinned && 'md:ml-60', // 240px = SIDEBAR_EXPANDED_WIDTH when pinned
          // Mobile: add top padding for mobile header
          'pt-14 md:pt-0',
          className
        )}
      >
        {/* Page Header */}
        {title && (
          <header className="flex-shrink-0 px-6 py-4 border-b border-border bg-white sticky top-14 md:top-0 z-30">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
                  {subtitle && (
                    <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
                  )}
                </div>
                {headerActions && (
                  <div className="flex items-center gap-3 flex-wrap">
                    {headerActions}
                  </div>
                )}
              </div>
            </div>
          </header>
        )}

        {/* Page Content - fills remaining space */}
        <div className={cn(
          'flex-1 min-h-0',
          fillHeight ? 'flex flex-col p-2' : 'p-6'
        )}>
          <div className={cn(
            fillHeight ? 'flex-1 min-h-0 flex flex-col w-full' : 'max-w-6xl mx-auto'
          )}>
            {children}
          </div>
        </div>

        {/* Footer */}
        {showFooter && <Footer />}
      </main>
    </div>
  )
}
